import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';

/**
 * A "?" icon that holds secondary explanatory text behind an interaction
 * (Condense information #390).
 *
 *   • pointer devices — the bubble appears while the mouse is over the icon;
 *   • keyboard        — it appears on focus (so it is reachable without a mouse);
 *   • touch / pen     — a tap toggles it, and it goes away on a tap anywhere else.
 *
 * Touch is handled by `click` alone and the hover path ignores `pointerType`
 * 'touch'/'pen', because a phone synthesises mouseenter + click for one tap: the
 * hover half would open it and the click half would toggle it straight back shut.
 * A click also PINS the bubble open (so a mouse user can read it without keeping
 * the cursor parked on a 14px icon); an outside press, Escape, or toggling the
 * icon again releases it.
 *
 * The bubble is portaled to <body> and positioned `fixed` from the icon's rect:
 * the Settings card scrolls and a `.pai-panel` clips absolute children, and a
 * transformed ancestor would size a fixed child by itself (design-system rules,
 * #225/#249). It re-anchors — never closes — on scroll/resize, and is clamped to
 * the viewport, flipping above the icon when there is no room below.
 *
 * Escape is handled in the CAPTURE phase and stopped, so dismissing the bubble
 * does not also dismiss the modal underneath it (the #272 lesson).
 *
 * Click-only popover mode (#461 — the "The Title AI LLC" entity bubble): pass
 * `trigger` (the trigger's own content, replacing the "?" icon) and
 * `hoverable={false}`. Hover and keyboard-focus no longer open it; only a click
 * (Enter/Space on a keyboard) toggles it, with the same outside-press / Escape
 * dismissal, portal and positioning. `bubbleRole="dialog"` is right when the
 * bubble holds something interactive (a link) rather than pure description.
 */

const BUBBLE_WIDTH = 280;
const EDGE = 8;
const GAP = 8;

const InfoTooltip = ({
    text,
    label,
    testId = 'info-tooltip',
    bubbleTestId = 'info-tooltip-bubble',
    className = '',
    trigger = null,
    hoverable = true,
    bubbleRole = 'tooltip',
}) => {
    const id = useId();
    const triggerRef = useRef(null);
    const bubbleRef = useRef(null);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [pos, setPos] = useState(null);
    const open = hovered || focused || pinned;

    const close = useCallback(() => {
        setHovered(false);
        setFocused(false);
        setPinned(false);
    }, []);

    const reposition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const r = trigger.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const width = Math.min(BUBBLE_WIDTH, vw - EDGE * 2);
        let left = r.left + r.width / 2 - width / 2;
        left = Math.max(EDGE, Math.min(left, vw - EDGE - width));
        const bubbleH = bubbleRef.current?.offsetHeight || 0;
        const below = r.bottom + GAP;
        const above = r.top - GAP - bubbleH;
        // Prefer below; flip above only when below overflows AND above fits.
        let top = below + bubbleH > vh - EDGE && above >= EDGE ? above : below;
        top = Math.max(EDGE, Math.min(top, vh - EDGE - bubbleH));
        setPos({ top, left, width });
    }, []);

    useLayoutEffect(() => {
        if (!open) { setPos(null); return undefined; }
        reposition();
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);
        return () => {
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
    }, [open, reposition]);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            const t = e.target;
            if (triggerRef.current?.contains(t) || bubbleRef.current?.contains(t)) return;
            close();
        };
        const onKey = (e) => {
            if (e.key === 'Escape') { e.stopPropagation(); close(); }
        };
        document.addEventListener('pointerdown', onDown, true);
        window.addEventListener('keydown', onKey, true);
        return () => {
            document.removeEventListener('pointerdown', onDown, true);
            window.removeEventListener('keydown', onKey, true);
        };
    }, [open, close]);

    const isHoverPointer = (e) => hoverable && e.pointerType !== 'touch' && e.pointerType !== 'pen';
    const triggerClass = trigger
        ? className
        : `pai-tap inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-pai-meta transition-colors hover:text-white focus-visible:text-white ${className}`;
    const isTooltip = bubbleRole === 'tooltip';

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                data-testid={testId}
                aria-label={label}
                aria-expanded={open}
                aria-describedby={open && isTooltip ? id : undefined}
                aria-controls={open && !isTooltip ? id : undefined}
                aria-haspopup={isTooltip ? undefined : 'dialog'}
                onPointerEnter={(e) => { if (isHoverPointer(e)) setHovered(true); }}
                onPointerLeave={(e) => { if (isHoverPointer(e)) setHovered(false); }}
                onFocus={(e) => {
                    // Keyboard focus only — a mouse/touch press focuses the button
                    // too, and that must not fight the click toggle below.
                    let keyboard = true;
                    try { keyboard = e.target.matches(':focus-visible'); } catch (_) { /* old engine: assume keyboard */ }
                    if (keyboard && hoverable) setFocused(true);
                }}
                onBlur={() => setFocused(false)}
                onClick={() => setPinned((v) => !v)}
                className={triggerClass}
            >
                {trigger || <HelpCircle size={14} />}
            </button>
            {open && createPortal(
                <div
                    ref={bubbleRef}
                    id={id}
                    role={bubbleRole}
                    aria-label={isTooltip ? undefined : label}
                    data-testid={bubbleTestId}
                    data-for={testId}
                    style={{
                        position: 'fixed',
                        top: pos ? pos.top : 0,
                        left: pos ? pos.left : 0,
                        width: pos ? pos.width : BUBBLE_WIDTH,
                        visibility: pos ? 'visible' : 'hidden',
                    }}
                    className="z-[90] rounded-card border border-pai bg-pai-modal p-3.5 text-[12.5px] leading-relaxed text-pai-secondary shadow-modal"
                >
                    {text}
                </div>,
                document.body,
            )}
        </>
    );
};

export default InfoTooltip;
